
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { mapGetters, mapActions } from "vuex";

import Project from "@/model/Project";
// import DateToIsoString from "@/utils/DateToIsoString"; // nicht benötigt
import { ActionMessage } from "@/model/Messages/statusMessage";
import DateToIsoString from "@/utils/DateToIsoString";

interface Status {
  class: string;
  msg: string;
}

@Component({
  name: "APIListTabView",
  computed: {
    ...mapGetters(["getUserAccess"]),
    ...mapGetters("projects", {
      CurrentProject: "GetCurrentProject"
    })
  },
  methods: {
    ...mapActions(["getAllDevicesFromBackEnd"]),
    ...mapActions("projects", {
      updateCurrentProject: "UpdateCurrentProject",
      DetailsOfProject: "DetailsOfProject",
      GetAllProjectsFromBackEnd: "GetAllProjectsFromBackEnd",
      GetAllProjectStatus: "GetAllStatusFromBackEnd",
      GetAllCountrys: "GetCountryList",
      GetAllUser: "GetAllUsersFromTheProject",
      getAllBatteryDevicesInProject: "getAllBatteryDevicesInProject"
    })
  }
})
export default class APIListTabView extends Mixins() {
  /* Props */
  @Prop({ type: Boolean, default: false }) isMainProject!: boolean;
  @Prop({ type: Boolean, default: false }) isMetaDataMainProject!: boolean;
  @Prop({ type: [Number, String], default: null }) mainProjectId!: number | string;

  /* Vuex */
  readonly CurrentProject!: Project;
  readonly getUserAccess!: any;
  private updateCurrentProject!: Function;
  private DetailsOfProject!: Function;
  private getAllDevicesFromBackEnd!: Function;
  private getAllBatteryDevicesInProject!: Function;

  /* Local state */
  show = true;
  showPassword = false;
  loading = false;

  updateStatus: null | ActionMessage = null;

  // UI-Time (HH:mm:ss) – wie Start-/Endzeit: nur die Zeit anzeigen,
  // aber beim Speichern wieder in das ursprüngliche ISO-Format einsetzen
  apiExportTime: string = "";

  // Teile des ursprünglichen ISO-Strings (Datum + evtl. Suffix wie 'Z' oder Offset)
  private apiExportDatePrefix: string | null = null;
  private apiExportSuffix: string = "";

  /* -------------------- Token vs Credentials Logik -------------------- */
  get isTokenMode(): boolean {
    const t = (this.CurrentProject.apiToken || "").trim();
    return t.length > 0;
  }
  get isCredsMode(): boolean {
    const u = (this.CurrentProject.apiUser || "").trim();
    const p = (this.CurrentProject.apiPassword || "").trim();
    return (u.length > 0 || p.length > 0) && !this.isTokenMode;
  }
  get disableToken(): boolean {
    return this.isCredsMode;
  }
  get disableCreds(): boolean {
    return this.isTokenMode;
  }
  get requiredApiToken(): boolean {
    return !!this.CurrentProject.apiExportActive && this.isTokenMode;
  }
  get requiredApiUser(): boolean {
    return !!this.CurrentProject.apiExportActive && this.isCredsMode;
  }
  get requiredApiPassword(): boolean {
    return !!this.CurrentProject.apiExportActive && this.isCredsMode;
  }
  get requiredApiCert(): boolean {
    return !!this.CurrentProject.apiExportActive && this.isCredsMode;
  }

  @Watch("CurrentProject.apiUser")
  onApiUserChanged(val: string) {
    if ((val || "").trim().length > 0) {
      this.CurrentProject.apiToken = "";
    }
  }
  @Watch("CurrentProject.apiPassword")
  onApiPasswordChanged(val: string) {
    if ((val || "").trim().length > 0) {
      this.CurrentProject.apiToken = "";
    }
  }
  @Watch("CurrentProject.apiToken")
  onApiTokenChanged(val: string) {
    if ((val || "").trim().length > 0) {
      this.CurrentProject.apiUser = "";
      this.CurrentProject.apiPassword = "";
    }
  }

  /* -------------------- Lifecycle -------------------- */
  async created() {
    if (this.CurrentProject.apiExportTime) {
      const iso = this.CurrentProject.apiExportTime;

      // Falls es noch im Format "YYYY-MM-DDTHH:MM:SS..." vorliegt → Date, Time, Suffix extrahieren
      const parts = this.splitIsoDateTime(iso);
      if (parts) {
        this.apiExportDatePrefix = parts.date;   // z.B. "2025-11-28"
        this.apiExportSuffix = parts.suffix;     // z.B. "Z" oder "+01:00"
        this.apiExportTime = parts.time;         // z.B. "00:15:00"
      } else {
        // sonst direkt übernehmen (z. B. "00:15:00" oder "00:15")
        this.apiExportTime = iso;
      }
    } else {
      this.apiExportTime = "00:00:00";
    }

    await this.getAllDevicesFromBackEnd();
    await this.getAllBatteryDevicesInProject(this.CurrentProject.id);
  }
  /* -------------------- Helpers -------------------- */
  private normalizeTimeStr(t: string): string {
    const [H = "00", M = "00", S] = (t || "").split(":");
    const hh = H.padStart(2, "0");
    const mm = M.padStart(2, "0");
    const ss = (S !== undefined && S !== "" ? S : "00").padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }

  private splitIsoDateTime(iso: string): { date: string; time: string; suffix: string } | null {
    const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(.*)$/);
    if (!m) return null;
    return {
      date: m[1],
      time: m[2],
      suffix: m[3] || ""
    };
  }
  /* -------------------- Submit -------------------- */
  async updateProject() {
    this.loading = true;

    // Eingabe aus dem Time-Input normalisieren (z. B. "00:15" -> "00:15:00")
    const normalizedTime = this.normalizeTimeStr(this.apiExportTime);

    // Fallback: wenn wir kein Datum aus der DB hatten, nimm heutiges Datum
    const datePrefix =
      this.apiExportDatePrefix || new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    // Suffix (z. B. Z oder +01:00) nur nutzen, wenn wir eins hatten
    const suffix = this.apiExportSuffix || "";

    // Finale API-Export-Time: YYYY-MM-DDTHH:mm:ss(+Suffix)
    this.CurrentProject.apiExportTime = `${datePrefix}T${normalizedTime}${suffix}`;

    const updatedProjectInformation = this.CurrentProject.formatToUpdate();
    const updateProjectStatus = await this.updateCurrentProject(updatedProjectInformation);

    if (updateProjectStatus.status === 200) {
      this.updateStatus = { class: "success", msg: this.$t("projects.statusMsg.updateSuccess") };
    } else if (updateProjectStatus.response?.status === 400) {
      this.updateStatus = { class: "danger", msg: updateProjectStatus.response.data.message };
    } else if (updateProjectStatus.response?.status === 403) {
      this.updateStatus = {
        class: "danger",
        msg:
          (this as any).$t(updateProjectStatus.response.data.label) +
          " [" +
          updateProjectStatus.response.data.message.split("[").pop()
      };
    } else {
      this.updateStatus = { class: "danger", msg: (this as any).$t("archiveProjects.actions.action400res") };
    }

    this.show = !this.show;
    this.loading = false;
  }


  /* -------------------- Actions / Misc -------------------- */
  async openEditMetadataForm() {
    this.show = !this.show;
  }

  handleUpdateProjectCancel() {
    this.loading = true;
    if (this.isMainProject) {
      this.DetailsOfProject(this.mainProjectId).then(() => {
        this.loading = false;
      });
      this.$bvModal.hide("editMainProject");
      if (this.isMetaDataMainProject) this.show = !this.show;
    } else {
      this.DetailsOfProject(this.$route.params.id).then(() => {
        this.loading = false;
      });
      this.show = !this.show;
    }
  }

  /* -------------------- Validation -------------------- */
  get isTheFormValid() {
    if (this.isMainProject) {
      return !this.CurrentProject.status || !this.CurrentProject.country;
    } else {
      const requiresApi = this.CurrentProject.apiExportActive;
      const tokenValid = this.isTokenMode && !!this.CurrentProject.apiToken;
      const credsValid =
        this.isCredsMode &&
        !!this.CurrentProject.apiUser &&
        !!this.CurrentProject.apiPassword;
      const authValid = tokenValid || credsValid;

      const missingRequired =
        !this.CurrentProject.apiUrl ||
        !this.apiExportTime ||
        !authValid;

      return requiresApi ? missingRequired : false;
    }
  }

  handleCloseOfTheAlert() {
    this.updateStatus = null;
  }
}
